import { useEffect, useState } from "react";
import { Button, Checkbox, Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, FormField } from "@/components/ui";
import { getErrorMessage } from "@/api/client";
import { toastError, toastSuccess } from "@/components/ui/Toast";
import { useCreateTerm } from "@/api/hooks/useClassRegistration";

interface TermFormDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated?: (termId: string) => void;
}

/** Create-term form. The server 422s when end_date <= start_date
 * (app/api/v2/class_registration.py::create_term) — surfaced verbatim via
 * `serverError` rather than re-derived client-side, same "server decides"
 * discipline as CreateBookingDialog.tsx. */
export function TermFormDialog({ open, onClose, onCreated }: TermFormDialogProps) {
  const createTerm = useCreateTerm();

  const [name, setName] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [monthlyBilling, setMonthlyBilling] = useState(true);
  const [serverError, setServerError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName("");
      setStartDate("");
      setEndDate("");
      setMonthlyBilling(true);
      setServerError(null);
    }
  }, [open]);

  const canSubmit = !!name.trim() && !!startDate && !!endDate && !createTerm.isPending;

  async function handleSubmit() {
    if (!canSubmit) return;
    setServerError(null);
    try {
      const term = await createTerm.mutateAsync({
        name: name.trim(),
        start_date: startDate,
        end_date: endDate,
        monthly_billing: monthlyBilling,
      });
      toastSuccess("Term added");
      onCreated?.(term.id);
      onClose();
    } catch (err) {
      const message = getErrorMessage(err);
      setServerError(message);
      toastError("Couldn't add term", message);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} ariaLabel="New term">
      <DialogContent size="sm">
        <DialogHeader onClose={onClose}>New term</DialogHeader>
        <DialogBody className="space-y-4">
          <FormField
            label="Name"
            required
            placeholder="e.g. Fall 2027"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <FormField
            label="Start date"
            type="date"
            required
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <FormField
            label="End date"
            type="date"
            required
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
          <Checkbox
            label="Bill monthly"
            checked={monthlyBilling}
            onChange={(e) => setMonthlyBilling(e.target.checked)}
          />
          {serverError && (
            <p className="text-sm text-danger" role="alert">
              {serverError}
            </p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose} disabled={createTerm.isPending}>
            Cancel
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={!canSubmit}>
            {createTerm.isPending ? "Adding…" : "Add term"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
